import React, { ButtonHTMLAttributes, ComponentType, forwardRef, HTMLAttributes, MouseEvent, PropsWithChildren, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { RootState, useAppSelector } from '@/state/store';
import Box from '@mui/material/Box';
// import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { styled } from '@mui/material/styles';

import { Viewer } from '@/components/viewer';
import CheckTreeView, { NodeSelectionChange } from '@/components/CheckTreeView';
import { ModelData } from '@/state/types/model-data';
import Sidebar from '@/components/Sidebar';
import CheckableCircularProgress from '@/components/CheckableCircularProgress';
import PendingIcon from '@/components/icons/Pending';
import hinted from '@/components/Hinted';

import './ProjectViewer.css';
import { ProjectData } from '@/state/types/project-data';
import { Section } from '@/state/types/section';
import Span from '@/components/Span';
import { saveExpandedSections, saveSelectedModels, saveSidebarWidth } from '@/actions/client';
import { createSelector } from '@reduxjs/toolkit';
import { saveThumbnails } from '@/actions/project';
import { Vector3d, ViewParams } from '@bimdata/definitions/types';
import { setSetting } from '@/actions/client';
import { ProgressInfo, ViewerControllerInterface } from '@bimdata/viewer-react';
import t from '@/utils/translate';
import { Logger } from '@bimdata/client';
import { BoundBox } from '@bimdata/definitions/dto';
import { ImplicitProjectThumbnails } from '@/state/authentication.slice';

type StyledDivElement = ComponentType<HTMLAttributes<HTMLDivElement>>;

type Rect = {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
};

type ProjectAreaProps = Rect & HTMLAttributes<HTMLDivElement>;

const ProjectArea = styled('div')(({ top, left, bottom, right }: ProjectAreaProps) => ({
  position: 'fixed',
  top: `${top || 0}px`, //113
  bottom: `${bottom || 0}px`,
  left: `${left || 0}px`,
  right: `${right || 0}px`,
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'start',
}));

const SidebarTop: StyledDivElement = styled('div')(`
  height: 100%;
  margin: 0;
  padding: 0;
  padding-bottom: 60px;
  box-sizing: border-box;
`);

const SidebarContainer: StyledDivElement = styled('div')(`
  height: 100%;
  margin: 0;
  padding: 10px 10px 20px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
`);

const SidebarFooter: StyledDivElement = styled('div')(`
  height: 60px;
  padding: 0;
  margin: 0;
  margin-top: -60px;
  box-sizing: border-box;
  background-color: #ffffff;
  position: relative;
  box-shadow: 0px -6px 7px rgba(0, 0, 0, 0.04);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
`);

const ShowModelsButton: ComponentType<ButtonHTMLAttributes<HTMLButtonElement>> = styled('button')({
  cursor: 'pointer',
  width: '100%',
  maxWidth: '270px',
  minWidth: 'fit-content',
  margin: '0 15px',
  padding: '6px 10px',
  height: '32px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '-0.02em',
  color: '#FFFFFF',
  background: '#0052CC',
  borderRadius: '6px',
  border: 'none',

  '&:disabled': {
    color: '#C1C7D0',
    background: '#EBECF0',
    border: '1px solid #DFE1E6',
  }
});

const Pending = styled(PendingIcon)({
  color: '#091E42',
  opacity: 0.5,
  width: '24px',
  height: '24px',
  padding: '4px',
});

const PendingHinted = hinted(styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;`,
  {}
);

const PendingStatus = () => (<PendingHinted hint={t('model.state.import')}><Pending /></PendingHinted>);

const SectionStats: ComponentType<HTMLAttributes<HTMLSpanElement>> = styled('span')(() => ({
  margin: '0px 4px',
  padding: '2px 6px',
  borderRadius: '5px',
  border: '1px solid #EBECF0',
  color: '#6B778C',
  fontSize: '12px',
  '&>em': {
    color: '#0065FF',
    fontStyle: 'normal'
  }
}));

const ModelsTreeTitle: ComponentType<HTMLAttributes<HTMLSpanElement> & { disabled?: boolean }> = styled(Span)((props: { disabled?: boolean }) => ({
  userSelect: 'none',
  fontSize: '14px',
  padding: '0 0 0 2px',
  display: 'inline-block',
  ...(!props.disabled
    ? {
      cursor: 'pointer',
      '&:hover': { color: '#0052CC' }
    }
    : { cursor: 'default' }
  )
}));

const sortedArraysEqual = (a: number[] | null, b: number[] | null): boolean => {
  if (!!a != !!b) return false;
  if (!!a && !!b) {
    if (a.length != b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] != b[i]) return false;
    }
  }
  return true;
}

const projectModels = (p: ProjectData): Set<number> =>
  (p.models || []).reduce((a, s) => {
    (s.models || []).forEach((m) => {
      if (m.ready && !m.failed) {
        a.add(m.id);
      }
    });
    return a;
  }, new Set<number>());


export type ProjectViewerProps = {
  project: ProjectData,
  accessToken?: string;
  generateThumbnails?: true;
  modelValidator?: (model: ModelData) => boolean;
} & Rect & HTMLAttributes<HTMLDivElement> & PropsWithChildren;

const idGetter = (n: Section | ModelData): string | number => (n as ModelData).id || n.name!;

const iconGetter = (n: Section | ModelData): ReactNode => {
  if ((n as ModelData).id) {
    if (!n.ready) {
      return <PendingStatus />;
    }
  }
  return null;
};

const hasCheckboxGetter = (n: Section | ModelData): boolean => (n as ModelData).id ? (n as ModelData).ready : !!(n as Section).models?.length;

const childrenGetter = (n: Section | ModelData): null | ModelData[] => {
  if (Array.isArray((n as Section).models)) {
    return (n as Section).models;
  }
  return null;
};


export default forwardRef<HTMLDivElement, ProjectViewerProps>(({ project, children, accessToken, generateThumbnails, modelValidator, ...props }, ref) => {
  const [load3D, setLoad3D] = useState(false);
  const [load3DProgress, setLoad3DProgress] = useState<Map<number, number>>(new Map());

  const [viewParams, setViewParams] = useState<ViewParams | undefined>(undefined);

  const controllerRef = useRef<ViewerControllerInterface | null>(null);

  const [sceneModels, setSceneModels] = useState<Map<number, BoundBox>>(new Map());

  const { selectedModels, expandedSections, sidebarWidth, ImplicitProjectThumbnails } = useAppSelector(
    createSelector(
      [
        (state: RootState): number[] => state.authentication.clientState.selectedModels[project.code],
        (state: RootState): Record<string, boolean> => state.authentication.clientState.expandedSections[project.code],
        (state: RootState): number => state.authentication.clientState.sidebarWidth,
        (state: RootState): Record<string, ImplicitProjectThumbnails> => state.authentication.clientState.ImplicitProjectThumbnails,
      ],
      (selectedModels, expandedSections, sidebarWidth, ImplicitProjectThumbnails) => ({
        selectedModels: selectedModels || [],
        expandedSections: expandedSections || {},
        sidebarWidth,
        ImplicitProjectThumbnails
      })
    )
  );

  const [storedSelection, setSelection] = useState<number[] | null>(null);
  const [filter, setFilter] = useState<number[] | null>(null);
  const [loadedModels] = useState<{ list: number[] }>({ list: [] });

  let selection: number[] = [];
  if (storedSelection == null) {
    const models = projectModels(project);
    selection = selectedModels.filter((m: number) => models.has(m));
  } else {
    selection = storedSelection;
  }

  const applyFilter = useCallback(() => {
    const models = projectModels(project);
    const f = selection.filter((id) => models.has(id)).sort();
    setViewParams(undefined);
    setFilter(f);
    setLoad3D(true);
  }, [project, selection]);

  const onChanges = useCallback((changes: NodeSelectionChange<Section | ModelData>[]) => {
    const models = projectModels(project);
    const sln = new Set(selection.filter((id) => models.has(id)));

    for (const c of changes) {
      const m = c.node as ModelData;
      if (!m.id) continue;
      if (c.value) {
        sln.add(m.id);
      } else {
        sln.delete(m.id);
      }
    }

    const newSelection = Array.from(sln).sort();
    setSelection(newSelection);
    saveSelectedModels(project.code, newSelection).catch((err) => {
      Logger.error(err);
    });
  }, [project, selection]);

  const calculateLoadingProgress = useCallback((n: Section | ModelData) => {
    let progress = 0;
    if (!(n as ModelData).id) {
      const s: number[] = (n as Section).models.filter((m) => selection.includes(m.id)).map(({ id }) => id);
      progress = s.reduce<number>(
        (a, id) => (a + (loadedModels.list.includes(id) ? 100 : ((load3D && load3DProgress.get(id)) || 0))),
        0
      )/s.length;
    } else {
      const mid = (n as ModelData).id;
      progress = loadedModels.list.includes(mid) ? 100 : ((load3D && load3DProgress.get(mid)) || 0);
    }
    return progress;
  }, [ selection, loadedModels, load3D, load3DProgress ]);

  const checkboxIconGetter= useCallback((n: Section | ModelData, checked: boolean) => (
    <CheckableCircularProgress progress={calculateLoadingProgress(n)} checked={checked} small={!!(n as ModelData).id}/>
  ), [calculateLoadingProgress]);


  const saveExpanded = useCallback((n: Section | ModelData, expanded: boolean) => {
    const t = String((n as ModelData).id || n.name);
    saveExpandedSections(project.code, { [t]: expanded }).catch((err) => {
      Logger.error(err);
    });
  }, [project.code]);

  const isActiveGetter = useCallback(() => !load3D, []);

  const initialValueGetter = useCallback((n: Section | ModelData) => (n as Section).models ? null : selection.includes((n as ModelData).id), [selection]);

  const initialExpandedState = useCallback((n: Section | ModelData) => {
    const t = String((n as ModelData).id || n.name);
    if (t in expandedSections) {
      return expandedSections[t];
    }
    return true;
  }, [project.code, expandedSections]);

  const canApplyFilter = selection.length && !sortedArraysEqual(selection, filter);

  const onSidebarResize = useCallback((width: number) => saveSidebarWidth(width), []);

  const roots = useMemo(
    () => project.models?.map((s) => ({
      ...s,
      models: (modelValidator ? s.models.filter(modelValidator) : s.models).sort((a, b) => a.name.localeCompare(b.name))
    })).filter((s) => !!s.models.length),
    [project.models, modelValidator]
  );

  /*
  const modelNames = useMemo(() => current?.models?.reduce<Map<number, string>>((m, s) => {
    s.models?.forEach(({ id, name }) => {
      m.set(id, name);
    });
    return m;
  }, new Map()) || new Map(), [current?.models])
  */

  /*
    {current.access !== AccessLevel.READER && (
      <HeaderButton
        onClick={ () => { setUploadOpen(true) } }
        title={t('project.action.upload')}
      >
        <ImportModelsIcon style={{ width: '18px', height: '18px', fontSize: '18px'}} />
      </HeaderButton>
    )}

  */

  const onHome = useCallback(async (position: Vector3d, direction: Vector3d, controller: ViewerControllerInterface) => {
    let implicitThumb = ImplicitProjectThumbnails[project.code]?.enabled;
    if (typeof implicitThumb === 'undefined') implicitThumb = true;
    if (
      implicitThumb &&
      (loadedModels.list.length > (ImplicitProjectThumbnails[project.code]?.models || 0))
    ) {
      const blob = await controller.getBlob();
      if (blob) {
        await saveThumbnails(project.code, blob, position, direction, loadedModels.list);
      }
    }
  }, [ project ]);

  const onLoadFinished = useCallback((models?: number[]) => {
    if (models) {
      loadedModels.list = models;
      if (loadedModels.list.length && controllerRef.current) {
        controllerRef.current.getSceneModels().then((sm) => {
          setSceneModels(sm.reduce((m, { id, boundBox }) => {
            m.set(id, boundBox.scene);
            return m;
          }, new Map()));
          setLoad3DProgress(new Map());
          setLoad3D(false);
        }).catch((err) => {
          Logger.error(err);
        });
        return;
      }
    }
    setLoad3DProgress(new Map());
    setLoad3D(false);
  }, []);

  const onLoadProgress = useCallback(({ models }: ProgressInfo) => {
    setLoad3DProgress(models);
  }, []);

  const nameGetter = useCallback((n: ModelData | Section) => {
    const hint = '';//(n.ready && !n.failed) ? `${n.elements} / ${n.geometries}` : (n.failed ? n.error : '');

    if (!(n as ModelData).id) {
      const sc = (n as Section).models.reduce<number>(
        (s, { id }) => s + (selection.includes(id) ? 1 : 0),
        0
      );

      return <>
        <ModelsTreeTitle style={{ fontWeight: 600 }} title={hint}>{n.name || t('model.noSection')}</ModelsTreeTitle>
        <SectionStats>{sc && <em>{sc}</em>}/{(n as Section).models.length}</SectionStats>
      </>;
    }


    const nameClick = useCallback((e: MouseEvent) => {
      const bb = sceneModels.get((n as ModelData).id)
      if (bb && controllerRef.current) {
        e.preventDefault();
        e.stopPropagation();
        controllerRef.current.focusOnSpace(bb, true);
      }
    }, [sceneModels]);

    return (
      <ModelsTreeTitle
        title={hint}
        disabled={!sceneModels.has((n as ModelData).id)}
        style={
          !(n as ModelData).ready
          ? {
            opacity: '50%',
            color: 'inherit !important',
          }
          : {}
        }
        onClick={sceneModels.has((n as ModelData).id) ? nameClick : undefined}
      >
        {n.name}
      </ModelsTreeTitle>
    );
  }, [sceneModels]);

  return (
    <ProjectArea {...props} ref={ref}>
      {!!roots?.length && (<>
        <Sidebar width={sidebarWidth} onResize={onSidebarResize}>
          <SidebarTop>
            <SidebarContainer>
              <CheckTreeView<Section | ModelData>
                roots={ roots }
                idGetter = {idGetter}
                nameGetter = {nameGetter}
                checkboxIconGetter={checkboxIconGetter}
                iconGetter={iconGetter}
                hasCheckboxGetter={hasCheckboxGetter}
                childrenGetter = {childrenGetter}
                initialValueGetter={initialValueGetter}
                initialStateGetter={initialExpandedState}
                isActiveGetter={isActiveGetter}
                onChanges={onChanges}
                onToggle={saveExpanded}
                checkMode='toggler'
              />
            </SidebarContainer>
          </SidebarTop>
          <SidebarFooter>
            <ShowModelsButton
              onClick={applyFilter}
              disabled={load3D || !canApplyFilter}
            >
              {t('project.viewer.action.shown')}
            </ShowModelsButton>
          </SidebarFooter>
        </Sidebar>
        <Box sx={{ width: '100%' }}>
          <Viewer
            project={project}
            filter={filter}
            onLoadFinished={onLoadFinished}
            onLoadProgress={onLoadProgress}
            onHome={generateThumbnails && onHome}
            viewParams={viewParams}
            setSetting={setSetting}
            accessToken={accessToken}
            controllerRef={controllerRef}
          />
        </Box>
      </>)}
      {children}
    </ProjectArea>
  );
});
